import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import { not, and } from 'tio-ui/utilities';
import * as v from 'valibot';
import { on } from '@ember/modifier';
import { PhCheck, PhPencil, PhX, PhPlus } from 'ember-phosphor-icons';
import { Section } from 'tio-reimagined/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Form from 'tio-reimagined/components/form';
import Input from 'tio-reimagined/components/input';
import type { FormResult } from 'tio-reimagined/components/form';
import type EmailAddressModel from 'tio-data/models/email-address';
import type MatchParticipantModel from 'tio-data/models/match-participant';
import type Owner from '@ember/owner';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type StoreService from 'ember-data/store';

const emailValidationSchema = v.object({
  email: v.pipe(
    v.string('Email is required'),
    v.nonEmpty('Email is required'),
    v.regex(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, 'Invalid email format')
  ),
});

interface EmailFormData {
  email: string;
}

interface SyfDashboardPersonalEmailSignature {
  Args: {
    matchParticipant: MatchParticipantModel;
  };
}

class SyfDashboardPersonalEmailComponent extends Component<SyfDashboardPersonalEmailSignature> {
  @service declare partner: PartnerService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked emailAddress!: EmailAddressModel;
  @tracked isEditing: boolean = false;
  @tracked submitError = null;
  @tracked originalEmail: string;
  @tracked formData: EmailFormData;

  constructor(owner: Owner, args: SyfDashboardPersonalEmailSignature['Args']) {
    super(owner, args);
    this.setEmailAddress();
    this.originalEmail = this.args.matchParticipant?.emailAddress?.email || '';
    this.formData = { email: this.originalEmail };
  }

  get disableSaveEmail() {
    const valueChanged = this.originalEmail !== this.formData.email;
    // Also validate email format
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    const isValidEmail = emailRegex.test(this.formData.email);
    return !valueChanged || !isValidEmail;
  }

  validate = (data: EmailFormData) => {
    const r = v.safeParse(emailValidationSchema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  handleFormChange = (result: FormResult<EmailFormData>) => {
    this.formData = result.data;
  };

  saveEmail = dropTask(async () => {
    this.submitError = null;
    this.emailAddress.email = this.formData.email;
    this.args.matchParticipant.emailAddress = this.emailAddress;

    try {
      await this.args.matchParticipant.emailAddress.save();
      await this.args.matchParticipant.save();
    } catch (e) {
      this.submitError = e;
      console.error(e);
    } finally {
      this.originalEmail = this.formData.email;
      this.toggleEdit();
    }
  });

  handleSubmit = () => {
    this.saveEmail.perform();
  };

  toggleEdit = () => {
    this.isEditing = !this.isEditing;
  };

  cancel = () => {
    this.isEditing = false;
    this.formData = { email: this.originalEmail };
  };

  setEmailAddress = () => {
    if (this.args.matchParticipant?.emailAddress) {
      this.emailAddress = this.args.matchParticipant.emailAddress;
    } else if (this.sessionContext.user.personalEmail) {
      this.emailAddress = this.sessionContext.user.personalEmail;
    } else {
      this.emailAddress = this.store.createRecord('email-address', {
        contactType: 'PERSONAL',
        user: this.sessionContext.user,
        email: '',
        isActive: true,
      });
    }
  };

  <template>
    <Section data-test-personal-email-section>
      <:header>
        {{t "syf.dashboard.email.header"}}
      </:header>
      <:body>
        <div class="text-sm mb-4">
          {{t "syf.dashboard.email.content"}}
        </div>
        <div
          class="flex justify-center lg:justify-start my-2 mx-4"
          data-test-personal-email-container
        >
          {{#if (and (not this.formData.email) (not this.isEditing))}}
            <Button
              @appearance="outlined"
              @intent="default"
              @icon={{PhPlus}}
              @iconPlacement="start"
              {{on "click" this.toggleEdit}}
              class="font-semibold uppercase text-sm"
              data-test-add-email-button
            >
              {{t "syf.dashboard.email.add_email"}}
            </Button>
          {{else}}
            <Form
              @data={{this.formData}}
              @validate={{this.validate}}
              @onChange={{this.handleFormChange}}
              @onSubmit={{this.handleSubmit}}
              as |form|
            >
              <div class="flex items-start gap-2">
                {{#let (form.field "email") as |f|}}
                  <Input
                    @value={{f.value}}
                    @onChange={{f.onChange}}
                    @onBlur={{f.onBlur}}
                    @errors={{f.errors}}
                    title={{this.formData.email}}
                    @isDisabled={{not this.isEditing}}
                    class="text-center"
                    data-test-email-field
                  />
                {{/let}}

                {{#if this.isEditing}}
                  <div class="flex">
                    <Button
                      @appearance="outlined"
                      {{on "click" this.cancel}}
                      @isDisabled={{this.saveEmail.isRunning}}
                      title={{t "common.cancel"}}
                      data-test-cancel-button
                    >
                      <PhX @size="20" />
                    </Button>
                    <Button
                      type="submit"
                      @isDisabled={{this.disableSaveEmail}}
                      title={{t "common.save"}}
                      data-test-save-button
                    >
                      <PhCheck @size="20" />
                    </Button>
                  </div>
                {{else}}
                  <Button
                    @appearance="outlined"
                    @intent="primary"
                    {{on "click" this.toggleEdit}}
                    title={{t "common.edit"}}
                    class="mx-1"
                    data-test-edit-button
                  >
                    <PhPencil @size="20" />
                  </Button>
                {{/if}}
              </div>
            </Form>
          {{/if}}
        </div>
      </:body>
    </Section>
  </template>
}

export default SyfDashboardPersonalEmailComponent;
